import 'reflect-metadata'
import { IdbService } from '../services/idb.service.js'
import { AuthService } from '../services/auth-service.js'
import { Injector, runInInjectionContext } from '@angular/core'
import {
	AppTrpcService,
	AppTrpcServiceInjectionToken,
} from '../services/app-trpc.service.js'
import { CsrfService } from '../services/csrf.service.js'
import {
	ConsumerToSharedWorkerMessage,
	SharedWorkerToConsumerMessage,
	SubscribableSharedWorkerToConsumerMessage,
	SubscribableSharedWorkerToConsumerMessageParams,
	SubscribableSharedWorkerToConsumerMessagePickProperties,
} from './shared-worker-messages.js'
import { Observable, share, shareReplay, Subscription } from 'rxjs'
import { pick } from 'ramda'

class PortSpecificData {
	name: string
	subscriptions: Map<string, Subscription> = new Map()
}

export class SharedBackgroundWorker {
	portData = new Map<MessagePort, PortSpecificData>()
	authService: AuthService
	trpcService: AppTrpcService

	declarations$: Observable<any>
	private sharedObservables = new Map<string, Observable<any>>()

	constructor() {
		const injector = Injector.create({
			providers: [
				{
					provide: AppTrpcServiceInjectionToken,
					useClass: AppTrpcService,
					deps: [Injector, CsrfService],
				},
				{ provide: AuthService, useFactory: () => this.authService },
				{ provide: CsrfService, useClass: CsrfService },
				{ provide: IdbService, useClass: IdbService },
				{ provide: Injector, useFactory: () => injector },
			],
		})
		runInInjectionContext(injector, () => {
			this.authService = new AuthService()
			this.trpcService = injector.get(AppTrpcServiceInjectionToken)
		})

		this.authService.heartbeat$.subscribe()
		this.authService.startRenewalTimeout(Date.now())
	}

	onConnect(port: MessagePort) {
		const data = new PortSpecificData()
		this.portData.set(port, data)
		port.onmessage = (event) => this.onMessage(port, event.data)
		port.start()
	}

	private send(port: MessagePort, message: SharedWorkerToConsumerMessage) {
		port.postMessage(message)
	}

	private onMessage(port: MessagePort, message: ConsumerToSharedWorkerMessage) {
		const data = this.portData.get(port)!

		switch (message.type) {
			case 'connect':
				data.name = message.name
				console.log(
					`Connected to worker: ${data.name}; now ${this.portData.size} active connections`,
				)
				break

			case 'disconnect':
				data.subscriptions.forEach((sub) => sub.unsubscribe())
				port.close()
				this.portData.delete(port)
				console.log(
					`Disconnected from worker: ${data.name}; now ${this.portData.size} active connections`,
				)
				break

			case 'subscribe':
				// console.log('Subscribed to:', message.messageType)
				this.subscribe(
					port,
					message.messageType,
					message.params,
					message.pickProperties,
				)
				break

			default:
				console.error('Unknown message type:', message)
		}
	}

	private subscribe<
		T extends SubscribableSharedWorkerToConsumerMessage['type'],
	>(
		port: MessagePort,
		messageType: T,
		parameters: SubscribableSharedWorkerToConsumerMessageParams<T>,
		pickProperties: SubscribableSharedWorkerToConsumerMessagePickProperties<T>,
	) {
		const subscriptionKey = `${messageType}__${JSON.stringify(parameters)}`
		const data = this.portData.get(port)!
		const subscription = data.subscriptions.get(subscriptionKey)
		if (subscription) return // already subscribed

		switch (messageType) {
			case 'declarationsChanged':
				let sharedObservable = this.sharedObservables.get(subscriptionKey)
				if (!sharedObservable) {
					sharedObservable = this.trpcService
						.subscriptionAsObservable((c) => c.boInfo.getTypeDeclarations$, {
							moduleId: '*',
							executionContexts: '*',
							...parameters,
						})
						.pipe(shareReplay(1))
					this.sharedObservables.set(subscriptionKey, sharedObservable)
				}
				data.subscriptions.set(
					subscriptionKey,
					sharedObservable.subscribe({
						next: (declarations) => {
							if (pickProperties != '*') {
								declarations = pick(pickProperties, declarations)
							}
							// console.log('Sending declarations:', Object.keys(declarations))
							this.send(port, { type: 'declarationsChanged', declarations })
						},
						complete: () => {
							console.log('Subscription completed:', subscriptionKey)
							data.subscriptions.delete(subscriptionKey)
						},
						error: (error) => {
							console.error('Subscription error:', subscriptionKey, error)
							data.subscriptions.delete(subscriptionKey)
						},
					}),
				)
				break
		}
	}
}
